import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getAuthToken } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertTriangle, ChevronDown, ChevronLeft, ChevronRight, Clock3,
  FileSearch, RefreshCw, Server, ShieldCheck,
} from "lucide-react";

type LogLevel = "Error" | "Warning" | "Information";
type CaptureHealth = "Healthy" | "Degraded" | "Unknown";

interface ApplicationLog {
  id: string;
  timestamp: string;
  level: LogLevel;
  message: string;
  endpoint: string | null;
  details: string;
}

interface LogSource {
  kind: "azure-app-service-file" | string;
  host: string;
  slot: string;
  retentionDays: number;
  instanceScope: string;
}

interface ApplicationLogsResponse {
  items: ApplicationLog[];
  page: number;
  pageSize: number;
  total: number;
  hasMore: boolean;
  truncated: boolean;
  source: LogSource;
  scannedAt: string;
  captureHealth: CaptureHealth;
}

interface Filters {
  levels: LogLevel[];
  search: string;
  endpoint: string;
  from: string;
  to: string;
}

const PAGE_SIZE = 50;
const MAX_RANGE_MS = 14 * 24 * 60 * 60 * 1000;
const LEVELS: LogLevel[] = ["Error", "Warning", "Information"];

function localDateTimeValue(date: Date): string {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 23);
}

function sanitizedSearch(value: string): string {
  return value.replace(/[\u0000-\u001f\u007f]/g, " ").trim().slice(0, 200);
}

function buildLogQuery(filters: Filters, page: number): string {
  const params = new URLSearchParams();
  for (const level of filters.levels) params.append("levels", level);
  const search = sanitizedSearch(filters.search);
  const endpoint = sanitizedSearch(filters.endpoint);
  if (search) params.set("search", search);
  if (endpoint) params.set("endpoint", endpoint);
  params.set("from", new Date(filters.from).toISOString());
  params.set("to", new Date(filters.to).toISOString());
  params.set("page", String(page));
  params.set("pageSize", String(PAGE_SIZE));
  return params.toString();
}

function levelStyle(level: LogLevel): string {
  if (level === "Error") return "border-red-300 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200";
  if (level === "Warning") return "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200";
  return "border-sky-300 bg-sky-50 text-sky-800 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-200";
}

function captureHealthValue(value: unknown): CaptureHealth {
  return value === "Healthy" || value === "Degraded" ? value : "Unknown";
}

const CAPTURE_HEALTH_TEXT: Record<CaptureHealth, string> = {
  Healthy: "The latest file-capture attempt was confirmed. This does not verify Azure storage persistence or complete log history.",
  Degraded: "Current file capture is degraded. Readable historical logs may still be shown; they do not confirm that new events are being captured.",
  Unknown: "Current file capture has not been confirmed. Readable historical files alone do not prove healthy logging.",
};

function formatTimestamp(value: string): string {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "Invalid timestamp";
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hour12: false,
  }).format(date);
}

function LogRowsSkeleton() {
  return (
    <div aria-label="Loading application logs" data-testid="logs-loading" className="divide-y divide-border">
      {Array.from({ length: 5 }).map((_, index) => (
        <div key={index} className="grid gap-3 px-4 py-4 md:grid-cols-[190px_100px_minmax(0,1fr)]">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-5 w-20 rounded-full" />
          <div className="space-y-2"><Skeleton className="h-4 w-3/4" /><Skeleton className="h-3 w-1/3" /></div>
        </div>
      ))}
    </div>
  );
}

export default function ApplicationLogsPage() {
  const now = useMemo(() => new Date(), []);
  const [from, setFrom] = useState(() => localDateTimeValue(new Date(now.getTime() - 24 * 60 * 60 * 1000)));
  const [to, setTo] = useState(() => localDateTimeValue(now));
  const [levels, setLevels] = useState<LogLevel[]>([]);
  const [search, setSearch] = useState("");
  const [endpoint, setEndpoint] = useState("");
  const [applied, setApplied] = useState<Filters>(() => ({
    levels: [],
    search: "",
    endpoint: "",
    from: localDateTimeValue(new Date(now.getTime() - 24 * 60 * 60 * 1000)),
    to: localDateTimeValue(now),
  }));
  const [rangeMode, setRangeMode] = useState<"rolling" | "fixed">("rolling");
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<ApplicationLogsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [captureHealth, setCaptureHealth] = useState<CaptureHealth>("Unknown");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [requestIntent, setRequestIntent] = useState(0);
  const requestSequence = useRef(0);
  const rollingRefreshPending = useRef(false);

  const timezone = useMemo(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "local time"; }
    catch { return "local time"; }
  }, []);

  const queryString = useMemo(() => {
    try { return buildLogQuery(applied, page); }
    catch { return ""; }
  }, [applied, page]);

  const loadLogs = useCallback(async (manual = false) => {
    if (!queryString) return;
    const requestId = ++requestSequence.current;
    setError(null);
    setCaptureHealth("Unknown");
    setRefreshing(manual);
    setLoading(!manual);
    try {
      // Reuse the existing token lookup (including impersonation-tab precedence).
      // Read error JSON only to accept the fixed health enum, never error text.
      // Results remain in component memory, not a persistent/query cache.
      const token = getAuthToken();
      const response = await fetch(`/api/admin/application-logs?${queryString}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
        cache: "no-store",
      });
      const payload = await response.json().catch(() => null) as ApplicationLogsResponse | null;
      if (requestId !== requestSequence.current) return;
      setCaptureHealth(captureHealthValue(payload?.captureHealth));
      if (!response.ok) {
        setResult(null);
        setError(response.status === 401
          ? "Sign in to view application logs."
          : response.status === 403
            ? "Application logs are restricted to SuperAdmin."
            : "Azure App Service logs are unavailable. No local logs are displayed.");
        return;
      }
      if (payload?.source?.kind !== "azure-app-service-file") {
        throw new Error("The Azure App Service log source is unavailable. No local logs are displayed.");
      }
      setResult(payload);
    } catch {
      if (requestId !== requestSequence.current) return;
      setResult(null);
      setCaptureHealth("Unknown");
      setError("Azure App Service logs are unavailable. No local logs are displayed.");
    } finally {
      if (requestId === requestSequence.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [queryString]);

  useEffect(() => {
    const manualRefresh = rollingRefreshPending.current;
    rollingRefreshPending.current = false;
    void loadLogs(manualRefresh);
    return () => { requestSequence.current += 1; };
  }, [loadLogs, requestIntent]);

  function applyFilters() {
    setValidationError(null);
    const fromDate = new Date(from);
    const toDate = new Date(to);
    if (!from || !to || !Number.isFinite(fromDate.getTime()) || !Number.isFinite(toDate.getTime())) {
      setValidationError("Enter a valid start and end date/time.");
      return;
    }
    if (fromDate.getTime() > toDate.getTime()) {
      setValidationError("Start date/time must be earlier than the end date/time.");
      return;
    }
    if (toDate.getTime() - fromDate.getTime() > MAX_RANGE_MS) {
      setValidationError("The selected range cannot exceed 14 days.");
      return;
    }
    setExpandedId(null);
    setRangeMode("fixed");
    setApplied({ levels: [...levels], search: sanitizedSearch(search), endpoint: sanitizedSearch(endpoint), from, to });
    setPage(1);
  }

  function clearFilters() {
    const current = new Date();
    const lastDay = new Date(current.getTime() - 24 * 60 * 60 * 1000);
    const nextFrom = localDateTimeValue(lastDay);
    const nextTo = localDateTimeValue(current);
    setFrom(nextFrom);
    setTo(nextTo);
    setLevels([]);
    setSearch("");
    setEndpoint("");
    setValidationError(null);
    setExpandedId(null);
    setRangeMode("rolling");
    setApplied({ levels: [], search: "", endpoint: "", from: nextFrom, to: nextTo });
    setPage(1);
  }

  function refreshLogs() {
    if (rangeMode === "fixed") {
      // Refresh the existing query without changing the user-selected range.
      // Keeping this as a direct request avoids an effect-driven duplicate.
      void loadLogs(true);
      return;
    }

    const current = new Date();
    const rollingFrom = localDateTimeValue(new Date(current.getTime() - 24 * 60 * 60 * 1000));
    const rollingTo = localDateTimeValue(current);
    setFrom(rollingFrom);
    setTo(rollingTo);
    setExpandedId(null);
    // Move only the applied date window. Draft changes to other filters remain drafts.
    rollingRefreshPending.current = true;
    setApplied((currentFilters) => ({
      ...currentFilters,
      from: rollingFrom,
      to: rollingTo,
    }));
    setPage(1);
    setRequestIntent((intent) => intent + 1);
  }

  function toggleLevel(level: LogLevel) {
    setLevels((current) => current.includes(level) ? current.filter((item) => item !== level) : [...current, level]);
  }

  const rows = [...(result?.items ?? [])].sort((a, b) =>
    new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  const rangeStart = result && result.total ? (result.page - 1) * result.pageSize + 1 : 0;
  const rangeEnd = result ? Math.min(result.page * result.pageSize, result.total) : 0;
  const unavailable = !!error;

  return (
    <div className="mx-auto max-w-7xl space-y-5 p-4 sm:p-6" data-testid="application-logs-page">
      <header className="flex flex-col justify-between gap-4 border-b border-border/80 pb-5 sm:flex-row sm:items-start">
        <div className="flex gap-3">
          <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-primary/15 bg-primary/5 text-primary">
            <FileSearch className="h-5 w-5" aria-hidden="true" />
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-semibold tracking-tight">Application logs</h1>
              <span className="rounded-full border border-border bg-muted/50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">SuperAdmin</span>
            </div>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              Read-only diagnostics from the Azure App Service slot. Log text is rendered as plain text; no local process logs are used.
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={refreshLogs}
          disabled={loading || refreshing}
          data-testid="button-refresh-application-logs"
          aria-label="Refresh application logs"
        >
          <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} aria-hidden="true" />
          Refresh
        </Button>
      </header>

      <section aria-label="Log source" className="rounded-lg border border-border bg-card p-4" data-testid="logs-source">
        <div className="flex items-start gap-3">
          <div className={`mt-0.5 rounded-md p-2 ${unavailable ? "bg-amber-500/10 text-amber-700 dark:text-amber-300" : "bg-primary/5 text-primary"}`}>
            {unavailable ? <AlertTriangle className="h-4 w-4" aria-hidden="true" /> : <Server className="h-4 w-4" aria-hidden="true" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <h2 className="text-sm font-semibold">{unavailable ? "Azure log source unavailable" : "Azure App Service file logs"}</h2>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${unavailable ? "bg-amber-500/10 text-amber-800 dark:text-amber-300" : "bg-emerald-500/10 text-emerald-800 dark:text-emerald-300"}`}>
                {unavailable ? "Not connected" : "Read only"}
              </span>
            </div>
            {result ? (
              <>
                <p className="mt-1 break-all text-xs text-muted-foreground">
                  {result.source.host} · slot {result.source.slot} · {result.source.instanceScope}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
                  <span data-testid="text-log-retention">Up to {result.source.retentionDays} rolling log files</span>
                  <span data-testid="text-log-scanned-at">Scanned {formatTimestamp(result.scannedAt)}</span>
                  <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3" aria-hidden="true" /> Sanitized by log service</span>
                </div>
              </>
            ) : (
              <p className="mt-1 text-xs text-muted-foreground" data-testid="text-log-source-status">
                {unavailable
                  ? "This page requests Azure slot logs only. Azure is currently unavailable or not connected; no local fallback is shown."
                  : "Connecting to the Azure slot log source…"}
              </p>
            )}
            <div className="mt-3 border-t border-border pt-3" role="status" data-testid="logs-capture-health">
              <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
                captureHealth === "Healthy"
                  ? "border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200"
                  : captureHealth === "Degraded"
                    ? "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
                    : "border-border bg-muted text-muted-foreground"
              }`} data-testid="badge-log-capture-health">
                Capture: {captureHealth}
              </span>
              <p className="mt-1 text-xs text-muted-foreground" data-testid="text-log-capture-health">
                {CAPTURE_HEALTH_TEXT[captureHealth]}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-border bg-card p-4" aria-label="Log filters">
        <div className="mb-3 flex items-center justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold">Filter events</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">All levels are included when none are selected.</p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
            <span data-testid="text-log-range-mode">
              {rangeMode === "rolling" ? "Rolling last 24 hours · refresh follows now" : "Fixed range · refresh preserves selected times"}
            </span>
            <span>Times in {timezone}</span>
          </div>
        </div>
        <div className="grid gap-4 xl:grid-cols-[1fr_1fr_1.2fr_1.2fr]">
          <fieldset className="space-y-2">
            <legend className="mb-2 text-xs font-medium text-muted-foreground">Severity</legend>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {LEVELS.map((level) => (
                <label key={level} className="inline-flex cursor-pointer items-center gap-2 text-sm" data-testid={`filter-level-${level.toLowerCase()}`}>
                  <input
                    type="checkbox"
                    checked={levels.includes(level)}
                    onChange={() => toggleLevel(level)}
                    className="h-4 w-4 accent-primary"
                    aria-label={`Include ${level} logs`}
                  />
                  {level}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="space-y-2">
            <label htmlFor="logs-search" className="text-xs font-medium text-muted-foreground">Message contains</label>
            <Input id="logs-search" value={search} maxLength={200} placeholder="Search sanitized message…" onChange={(event) => setSearch(event.target.value)} onKeyDown={(event) => event.key === "Enter" && applyFilters()} data-testid="filter-log-search" />
            <p className="text-[10px] text-muted-foreground">{search.length}/200 characters</p>
          </div>
          <div className="space-y-2">
            <label htmlFor="logs-endpoint" className="text-xs font-medium text-muted-foreground">API endpoint contains</label>
            <Input id="logs-endpoint" value={endpoint} maxLength={200} placeholder="/api/…" onChange={(event) => setEndpoint(event.target.value)} onKeyDown={(event) => event.key === "Enter" && applyFilters()} data-testid="filter-log-endpoint" />
            <p className="text-[10px] text-muted-foreground">Substring match · up to 200 characters</p>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <label htmlFor="logs-from" className="text-xs font-medium text-muted-foreground">From</label>
              <Input id="logs-from" type="datetime-local" step="0.001" value={from} onChange={(event) => setFrom(event.target.value)} data-testid="filter-log-from" />
            </div>
            <div className="space-y-2">
              <label htmlFor="logs-to" className="text-xs font-medium text-muted-foreground">To</label>
              <Input id="logs-to" type="datetime-local" step="0.001" value={to} onChange={(event) => setTo(event.target.value)} data-testid="filter-log-to" />
            </div>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3">
          <div className="min-h-5 text-xs text-destructive" role="alert" data-testid="text-log-filter-error">{validationError}</div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="button-clear-log-filters">Reset</Button>
            <Button size="sm" onClick={applyFilters} data-testid="button-apply-log-filters">Apply filters</Button>
          </div>
        </div>
      </section>

      {result?.truncated && (
        <div role="status" className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200" data-testid="warning-logs-truncated">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <p><strong>Scanned window is truncated.</strong> The total and pages below cover only the scanned log window, not necessarily every retained event.</p>
        </div>
      )}

      <section className="overflow-hidden rounded-lg border border-border bg-card" aria-label="Application log results">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-muted/30 px-4 py-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Clock3 className="h-3.5 w-3.5" aria-hidden="true" />
            {loading ? "Loading Azure logs…" : result ? `Showing ${rangeStart}–${rangeEnd} of ${result.total.toLocaleString()} scanned events` : "Awaiting log results"}
          </div>
          {result && <div className="font-mono text-[10px] text-muted-foreground">Page {result.page} · {result.pageSize} per page</div>}
        </div>

        {loading ? <LogRowsSkeleton /> : unavailable ? (
          <div className="px-5 py-12 text-center" role="alert" data-testid="error-application-logs">
            <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-300">
              <AlertTriangle className="h-5 w-5" aria-hidden="true" />
            </div>
            <h3 className="mt-3 text-sm font-semibold">Could not load Azure slot logs</h3>
            <p className="mx-auto mt-1 max-w-xl text-xs text-muted-foreground">{error}</p>
            <p className="mt-2 text-[11px] text-muted-foreground">No local logs or sample events are substituted.</p>
            <Button className="mt-4" size="sm" variant="outline" onClick={refreshLogs} data-testid="button-retry-application-logs">Try again</Button>
          </div>
        ) : rows.length === 0 ? (
          <div className="px-5 py-12 text-center" data-testid="empty-application-logs">
            <FileSearch className="mx-auto h-7 w-7 text-muted-foreground/50" aria-hidden="true" />
            <h3 className="mt-3 text-sm font-semibold">No matching application logs</h3>
            <p className="mt-1 text-xs text-muted-foreground">Adjust the time range or filters, then apply again.</p>
          </div>
        ) : (
          <div className="divide-y divide-border" role="list" data-testid="list-application-logs">
            {rows.map((row) => {
              const isExpanded = expandedId === row.id;
              return (
                <article key={row.id} role="listitem" className="px-4 py-3.5 transition-colors hover:bg-muted/20" data-testid={`row-application-log-${row.id}`}>
                  <button
                    type="button"
                    className="grid w-full gap-2 text-left sm:grid-cols-[190px_100px_minmax(0,1fr)_20px] sm:items-start"
                    onClick={() => setExpandedId(isExpanded ? null : row.id)}
                    aria-expanded={isExpanded}
                    aria-controls={`details-${row.id}`}
                    data-testid={`button-expand-log-${row.id}`}
                  >
                    <time dateTime={row.timestamp} className="pt-0.5 font-mono text-[11px] text-muted-foreground" data-testid={`text-log-timestamp-${row.id}`}>{formatTimestamp(row.timestamp)}</time>
                    <span className={`w-fit rounded-full border px-2 py-0.5 text-[10px] font-semibold ${levelStyle(row.level)}`} data-testid={`badge-log-level-${row.id}`}>{row.level}</span>
                    <span className="min-w-0">
                      <span className="block break-words text-sm leading-relaxed">{row.message}</span>
                      <span className="mt-1 block truncate font-mono text-[10px] text-muted-foreground">{row.endpoint || "No endpoint"}</span>
                    </span>
                    <ChevronDown className={`hidden h-4 w-4 text-muted-foreground transition-transform sm:block ${isExpanded ? "rotate-180" : ""}`} aria-hidden="true" />
                  </button>
                  {isExpanded && (
                    <div id={`details-${row.id}`} className="mt-3 rounded-md border border-border bg-muted/30 p-3 sm:ml-[190px]" data-testid={`details-application-log-${row.id}`}>
                      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Sanitized details</div>
                      <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-words font-mono text-xs leading-relaxed">{row.details || "No additional details."}</pre>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}

        {!loading && !unavailable && result && (
          <div className="flex items-center justify-between gap-3 border-t border-border bg-muted/20 px-4 py-3">
            <span className="text-[11px] text-muted-foreground">Newest first · timestamp shown in {timezone}</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => { setExpandedId(null); setPage((value) => Math.max(1, value - 1)); }} disabled={page <= 1} data-testid="button-logs-previous-page" aria-label="Previous page">
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />Previous
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setExpandedId(null); setPage((value) => value + 1); }} disabled={!result.hasMore} data-testid="button-logs-next-page" aria-label="Next page">
                Next<ChevronRight className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          </div>
        )}
      </section>
      <p className="text-[10px] leading-relaxed text-muted-foreground">
        Diagnostic output may contain operational context. Review only as needed for support; the viewer does not render HTML, persist expanded details, or expose local development logs.
      </p>
    </div>
  );
}